import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faCamera, 
  faKey, 
  faEnvelope, 
  faPhone, 
  faIdCard,
  faBuilding,
  faHome,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../utils/constants';
import Navigation from '../Layouts/Navigation/Navigation';
import useAutoDismissSnack from '../Hooks/AutoDismissSnack';
import NavigationSecurity from '../Layouts/Navigation/NavigationSecurity';
import NavigationUser from '../Layouts/Navigation/NavigationUser';
import ImageCropDialog from './ImageCropDialog';
import { getCroppedImg } from './cropUtils';

function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  
  // Snackbar state
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackStatus, setSnackStatus] = useState('success');

  useAutoDismissSnack(openSnack, setOpenSnack);

  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    usuario: '',
    telefono: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const userData = JSON.parse(atob(token.split('.')[1]));
    loadProfile(userData.id_usuario);
  }, [navigate]);

  const loadProfile = (userId) => {
    axios.get(`${BASE_URL}/profile/${userId}`)
      .then(response => {
        setProfile(response.data);
        setFormData({
          nombre: response.data.nombre,
          apellido: response.data.apellido,
          email: response.data.email,
          usuario: response.data.usuario,
          telefono: response.data.telefono,
          password: '',
          confirmPassword: ''
        });
        setLoading(false);
      })
      .catch(error => {
        console.error('Error al cargar perfil:', error);
        setLoading(false);
      });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('La imagen no debe superar 5MB', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result);
        setShowCropDialog(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedAreaPixels) => {
    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      
      setAvatarFile(croppedFile);
      setAvatarPreview(URL.createObjectURL(croppedBlob));
      setShowCropDialog(false);
      setImageToCrop(null);
    } catch (error) {
      console.error('Error al recortar imagen:', error);
      showSnackbar('Error al procesar imagen', 'error');
    }
  };

  const handleCropCancel = () => {
    setShowCropDialog(false);
    setImageToCrop(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (changingPassword) {
      if (formData.password !== formData.confirmPassword) {
        showSnackbar('Las contraseñas no coinciden', 'error');
        return;
      }
      if (formData.password.length < 6) {
        showSnackbar('La contraseña debe tener al menos 6 caracteres', 'error');
        return;
      }
    }

    const updateData = {
      nombre: formData.nombre,
      apellido: formData.apellido,
      email: formData.email,
      usuario: formData.usuario,
      telefono: formData.telefono
    };

    if (changingPassword && formData.password) {
      updateData.password = formData.password;
    }

    try {
      await axios.put(`${BASE_URL}/profile/${profile.id_usuario}`, updateData);
      
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        const avatarResponse = await axios.post(
          `${BASE_URL}/profile/${profile.id_usuario}/avatar`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setProfile({ ...profile, avatar: avatarResponse.data.avatar });
        
        // Actualizar el token con el nuevo avatar
        const token = localStorage.getItem('token');
        if (token) {
          const userData = JSON.parse(atob(token.split('.')[1]));
          userData.avatar = avatarResponse.data.avatar;
          userData.nombre = updateData.nombre;
          userData.apellido = updateData.apellido;
          const newToken = token.split('.')[0] + '.' + btoa(JSON.stringify(userData)) + '.' + token.split('.')[2];
          localStorage.setItem('token', newToken);
          window.dispatchEvent(new Event('storage'));
        }
      }

      showSnackbar('Perfil actualizado correctamente', 'success');
      setEditing(false);
      setChangingPassword(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      loadProfile(profile.id_usuario);
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      showSnackbar('Error al actualizar perfil', 'error');
    }
  };

  const handleDeleteAvatar = async () => {
    if (window.confirm('¿Está seguro de eliminar su foto de perfil?')) {
      try {
        await axios.delete(`${BASE_URL}/profile/${profile.id_usuario}/avatar`);
        setProfile({ ...profile, avatar: null });
        showSnackbar('Foto de perfil eliminada', 'success');
      } catch (error) {
        console.error('Error al eliminar avatar:', error);
        showSnackbar('Error al eliminar foto', 'error');
      }
    }
  };

  const showSnackbar = (message, status) => {
    setSnackMessage(message);
    setSnackStatus(status);
    setOpenSnack(true);
  };

  const handleCloseSnack = () => {
    setOpenSnack(false);
  };

  const getNavigationComponent = () => {
    if (!profile) return <Navigation />;
    switch (profile.id_tipo_usuario) {
      case 1: return <Navigation />;
      case 2: return <NavigationSecurity />;
      case 3: return <NavigationUser />;
      default: return <Navigation />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">Cargando perfil...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-red-600">Error al cargar perfil</div>
      </div>
    );
  }

  const avatarUrl = avatarPreview || (profile.avatar ? `${BASE_URL}${profile.avatar}` : null);

  return (
    <>
      {getNavigationComponent()}
      
      {/* Image Crop Dialog */}
      {showCropDialog && (
        <ImageCropDialog
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Snackbar */}
          {openSnack && (
            <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${
              snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white animate-fade-in-down z-50`}>
              <div className="flex items-center gap-2">
                {snackStatus === 'success' ? (
                  <FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6" />
                ) : (
                  <FontAwesomeIcon icon={faTimesCircle} className="h-6 w-6" />
                )}
                <span className="font-semibold">{snackMessage}</span>
                <button onClick={handleCloseSnack} className="ml-2 hover:bg-white/20 rounded p-1">
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              Mi Perfil
            </h1>
            <p className="text-gray-600">Gestiona tu información personal y configuración</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Avatar Section */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-white shadow-lg ring-4 ring-white">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <FontAwesomeIcon icon={faUser} className="w-full h-full text-gray-300" />
                    )}
                  </div>
                  {editing && (
                    <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                      <FontAwesomeIcon icon={faCamera} className="h-5 w-5" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                <h2 className="mt-4 text-2xl font-bold text-white">
                  {profile.nombre} {profile.apellido}
                </h2>
                <p className="text-blue-100">@{profile.usuario}</p>
                <span className="mt-2 px-4 py-1 bg-white/20 text-white rounded-full text-sm">
                  {profile.tipo_usuario}
                </span>
              </div>
            </div>

            {/* Profile Information */}
            <div className="p-8">
              {!editing ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon icon={faUser} className="h-6 w-6 text-blue-600 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Nombre completo</p>
                        <p className="text-lg font-semibold">{profile.nombre} {profile.apellido}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon icon={faEnvelope} className="h-6 w-6 text-blue-600 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Correo electrónico</p>
                        <p className="text-lg font-semibold">{profile.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon icon={faIdCard} className="h-6 w-6 text-blue-600 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Identificación</p>
                        <p className="text-lg font-semibold">{profile.identificacion}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon icon={faPhone} className="h-6 w-6 text-blue-600 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Teléfono</p>
                        <p className="text-lg font-semibold">{profile.telefono}</p>
                      </div>
                    </div>
                    {profile.torre_nombre && (
                      <>
                        <div className="flex items-start gap-3">
                          <FontAwesomeIcon icon={faBuilding} className="h-6 w-6 text-blue-600 mt-1" />
                          <div>
                            <p className="text-sm text-gray-500">Torre</p>
                            <p className="text-lg font-semibold">{profile.torre_nombre}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <FontAwesomeIcon icon={faHome} className="h-6 w-6 text-blue-600 mt-1" />
                          <div>
                            <p className="text-sm text-gray-500">Apartamento</p>
                            <p className="text-lg font-semibold">
                              Apto {profile.num_apartamento} - Piso {profile.piso}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-4 pt-6">
                    <button
                      onClick={() => setEditing(true)}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
                    >
                      Editar Perfil
                    </button>
                    {profile.avatar && (
                      <button
                        onClick={handleDeleteAvatar}
                        className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Eliminar Foto
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Apellido *
                      </label>
                      <input
                        type="text"
                        name="apellido"
                        value={formData.apellido}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correo electrónico *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Usuario *
                      </label>
                      <input
                        type="text"
                        name="usuario"
                        value={formData.usuario}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setChangingPassword(!changingPassword)}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <FontAwesomeIcon icon={faKey} className="h-5 w-5" />
                      {changingPassword ? 'Cancelar cambio de contraseña' : 'Cambiar contraseña'}
                    </button>
                  </div>

                  {changingPassword && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nueva contraseña *
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          minLength={6}
                          required={changingPassword}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirmar contraseña *
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          minLength={6}
                          required={changingPassword}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 pt-6">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
                    >
                      Guardar Cambios
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(false);
                        setChangingPassword(false);
                        setAvatarPreview(null);
                        setAvatarFile(null);
                      }}
                      className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Profile;
